import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api/apiClient';
import { useUI } from '../../context/UIContext';

type FlagRow = {
  env: boolean;
  effective: boolean;
  overridden: boolean;
};

type Snapshot = {
  flags?: {
    stylistApproval?: FlagRow;
    stylistSettlementTransfersEnabled?: FlagRow;
  };
};

/**
 * Admin view: env vs DB override vs effective. Toggle calls PUT /api/v1/admin/feature-flags/:key (audited).
 */
const FeatureGatesPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/v1/admin/feature-flags');
      setSnapshot(res.data?.data ?? null);
    } catch {
      try {
        const pub = await api.get('/config/features');
        const d = pub.data?.data || {};
        setSnapshot({
          flags: {
            stylistApproval: {
              env: !!d.stylistApproval,
              effective: !!d.stylistApproval,
              overridden: false,
            },
          },
        });
      } catch {
        setSnapshot(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const row = snapshot?.flags?.stylistApproval;
  const transferRow = snapshot?.flags?.stylistSettlementTransfersEnabled;

  const toggle = async (key: 'stylistApproval' | 'stylistSettlementTransfersEnabled', next: boolean) => {
    setSaving(true);
    try {
      await api.put(`/v1/admin/feature-flags/${key}`, { value: next });
      showSnackbar('Feature flag updated', 'success');
      await load();
    } catch (e: any) {
      showSnackbar(e?.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <h1 style={{ marginBottom: 12 }}>Feature gates</h1>
      <p style={{ color: '#666', marginBottom: 20 }}>
        Effective value uses the database override when set; otherwise <code>ENABLE_STYLIST_APPROVAL</code>{' '}
        env. Updates are written to the audit trail.
      </p>
      {row ? (
        <div
          style={{
            border: '1px solid #eee',
            borderRadius: 10,
            padding: 16,
            background: '#fff',
          }}>
          <h3 style={{ marginTop: 0 }}>stylistApproval</h3>
          <p style={{ color: '#555', margin: '8px 0' }}>
            Env default: <strong>{row.env ? 'On' : 'Off'}</strong>
          </p>
          <p style={{ color: '#555', margin: '8px 0' }}>
            DB override active: <strong>{row.overridden ? 'Yes' : 'No'}</strong>
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, cursor: saving ? 'wait' : 'pointer' }}>
            <input
              type="checkbox"
              checked={row.effective}
              disabled={saving}
              onChange={(e) => toggle('stylistApproval', e.target.checked)}
            />
            <span>Effective (live)</span>
          </label>
        </div>
      ) : (
        <p>Could not load flags.</p>
      )}
      {transferRow ? (
        <div
          style={{
            border: '1px solid #eee',
            borderRadius: 10,
            padding: 16,
            background: '#fff',
            marginTop: 16,
          }}>
          <h3 style={{ marginTop: 0 }}>stylistSettlementTransfersEnabled</h3>
          <p style={{ color: '#555', margin: '8px 0' }}>
            Default off. The cutover script turns this on after manual payouts and wallet
            cutover both report zero failures. Do not enable before that script succeeds.
          </p>
          <p style={{ color: '#555', margin: '8px 0' }}>
            Env default: <strong>{transferRow.env ? 'On' : 'Off'}</strong>
          </p>
          <p style={{ color: '#555', margin: '8px 0' }}>
            DB override active: <strong>{transferRow.overridden ? 'Yes' : 'No'}</strong>
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, cursor: saving ? 'wait' : 'pointer' }}>
            <input
              type="checkbox"
              checked={transferRow.effective}
              disabled={saving}
              onChange={(e) => toggle('stylistSettlementTransfersEnabled', e.target.checked)}
            />
            <span>Effective (live)</span>
          </label>
        </div>
      ) : null}
    </div>
  );
};

export default FeatureGatesPage;
